import { OrderUnitOfMeasure, UnitOfDistance } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import * as yup from 'yup';

import { commissionPercentageSchema } from '~components/PayCommission/commissionPercentageSchema';
import { DEFAULT_MAX_LOADS_COUNT, SPLITTABLE_MAX_LOADS_COUNT } from '~constants/consts';
import {
  nameIdNonRequiredSchema,
  nameIdSchemaRequired,
  nameIdTypeSchema,
  notesSchema,
  phoneSchema,
  waypointSchema,
  waypointSchemaOptionalSites,
} from '~constants/regexConst';
import { Company, CompanyBasic } from '~hooks/useCompany/models';
import { CompanySettings } from '~hooks/useCompanySettings/models';
import { MaterialRateTypeahead } from '~hooks/useMaterialRates/models';
import { Order } from '~hooks/useOrders/models';
import { Service } from '~hooks/useServices/models';
import { UserBasic } from '~hooks/useUsers/models';
import {
  EmptyContactItem,
  makeRequiredDepartmentSchema,
  makeRequiredSalespeopleSchema,
  makeRequiredTaxSchema,
  TaxRateScheduleFormValue,
  toTaxRateScheduleFormValue,
} from '~pages/Sales/Projects/projectFormSchema';
import {
  isBlankRateValue,
  makeMaterialRateRequirement,
  makeRatePriceSchema,
  requiredMessage,
} from '~pages/Sales/sharedSales/rateRequirements';
import { getRateTypeLabel } from '~pages/Settings/RatesManagement/rateUtils';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import {
  convertMeters,
  formQuantityFieldRepresentsEmptyQuantity,
} from '~utils/utilFunctions';

import { fileAttachmentSchema } from '../../../formsShared/components/FileAttachments';

const DEFAULT_CYCLE_ONSITE_DURATION_IN_MINUTES = 20;
export const MAX_TRUCK_COUNT = 200;
export const DEFAULT_EQUIPMENT_TYPE_GROSS_CAPACITY = 18;

export const serviceIsStandingHaul = (service: { name?: string | null } | null) => {
  return service?.name === 'Standing Haul';
};
export const serviceIsHiredTruck = (service: Service | null) => {
  return service?.name === 'Hired Truck';
};

export const findHiredTruckServiceInServicesList = (list: Service[]) => {
  return list.find((service) => serviceIsHiredTruck(service));
};

const emptyStringOrNumberNotRequiredSchema = yup.lazy((val) => {
  return String(val).trim() === ''
    ? yup.string().notRequired()
    : yup.number().notRequired();
});

const newOrderFormSchema = yup.object().shape(
  {
    id: yup.string(),
    company: yup
      .object()
      .shape({
        legalName: yup.string(),
        id: yup.string(),
      })
      .when('id', {
        is: '',
        then: () =>
          yup.object().shape({
            legalName: yup.string().required(),
            id: yup.string().required(),
          }),
        otherwise: () => yup.object().notRequired(),
      }),
    jobStartAt: yup.string().required(),
    loadAt: yup.string().required(),
    orderId: yup.string(),
    notes: notesSchema,
    jobNotes: notesSchema,
    projectNotes: notesSchema,
    orderNotes: notesSchema,
    internalNotes: notesSchema,
    jobTime: yup.lazy((value) => {
      const isEmpty = isNil(value) || !String(value).trim().length;

      if (isEmpty) {
        return yup.string().notRequired();
      }

      return yup.number().min(0).required();
    }),
    truckCount: yup.number().when('id', {
      is: (id: string) => id,
      then: (schema) => {
        return schema.notRequired();
      },
      otherwise: (schema) => {
        return schema
          .min(1, `${t('form_validation_errors.minimum_is_num', { num: 1 })}`)
          .max(
            MAX_TRUCK_COUNT,
            `${t('form_validation_errors.maximum_is_num', { num: MAX_TRUCK_COUNT })}`,
          )
          .typeError('Numbers only.')
          .required();
      },
    }),
    truckDelay: emptyStringOrNumberNotRequiredSchema,
    trucksPerDelay: yup.lazy((val) => {
      return String(val).trim() === ''
        ? yup.string().notRequired()
        : yup
            .number()
            .min(1, () => t('form_fields.trucks_per_delay_min'))
            .nullable()
            .test(
              'is-not-greater-than-truckCount',
              `${t('form_validation_errors.trucks_per_delay_greater_than_truck_count')}`,
              (value, context) => {
                if (isNil(value)) return true;
                if (!context.parent.truckCount) return true;
                return value <= context.parent.truckCount;
              },
            );
    }),
    loadsPerTruck: yup.lazy((val) => {
      return String(val).trim() === ''
        ? yup.string().notRequired()
        : yup
            .number()
            .nullable()
            .min(1, `${t('form_validation_errors.minimum_is_num', { num: 1 })}`)
            .max(
              DEFAULT_MAX_LOADS_COUNT,
              `${t('form_validation_errors.maximum_is_num', { num: DEFAULT_MAX_LOADS_COUNT })}`,
            );
    }),
    unitOfMeasure: nameIdNonRequiredSchema.when(
      ['service', 'quantity'],
      ([service, quantity]) => {
        const isHiredTruckService = serviceIsHiredTruck(service);
        const hasEmptyQuantity = formQuantityFieldRepresentsEmptyQuantity(quantity);

        if (!hasEmptyQuantity) {
          return nameIdSchemaRequired.required();
        }

        if (isHiredTruckService) {
          return nameIdNonRequiredSchema.notRequired();
        }

        return nameIdSchemaRequired.required();
      },
    ),
    account: nameIdSchemaRequired.required(),
    project: nameIdNonRequiredSchema.nullable(),
    material: nameIdNonRequiredSchema.nullable().when('service', {
      is: (service: Service | null) => serviceIsHiredTruck(service),
      then: () => nameIdNonRequiredSchema.notRequired(),
      otherwise: () => nameIdSchemaRequired.required(),
    }),
    equipmentType: nameIdNonRequiredSchema.notRequired(),
    service: nameIdTypeSchema.required(),
    quantity: yup.number().when('service', {
      is: (service: Service | null) => {
        return serviceIsHiredTruck(service) || serviceIsStandingHaul(service);
      },
      then: (schema) => {
        return schema
          .nullable()
          .min(0)
          .transform((_, val) => (String(val).trim() !== '' ? Number(val) : null));
      },
      otherwise: (schema) => schema.min(0).required(),
    }),
    state: yup.string(),
    pickUpWayPoint: waypointSchemaOptionalSites.when('service', {
      is: (service: Service | null) => serviceIsHiredTruck(service),
      then: () => waypointSchemaOptionalSites.notRequired(),
      otherwise: () => waypointSchema.required(),
    }),
    dropOffWayPoint: waypointSchemaOptionalSites.when('service', {
      is: (service: Service | null) => serviceIsHiredTruck(service),
      then: () => waypointSchemaOptionalSites.notRequired(),
      otherwise: () => waypointSchema.required(),
    }),
    stagingWayPoint: waypointSchemaOptionalSites.notRequired(),
    phase: yup
      .object()
      .shape({
        id: yup.string(),
        name: yup.string(),
        code: yup.string(),
      })
      .notRequired(),
    phaseName: yup.string().notRequired(),
    phaseCode: yup.string().notRequired(),
    foremen: yup
      .array()
      .of(yup.object().shape({ id: yup.string().required() }).required()),
    collaborators: yup
      .array()
      .of(yup.object().shape({ id: yup.string().required() }).required()),
    salespeople: yup
      .array()
      .of(yup.object().shape({ id: yup.string().required() }).required()),
    allContacts: yup.array().of(
      yup.object().shape(
        {
          name: yup
            .string()
            .trim()
            .when('phone', {
              is: (phone: string) => Boolean(phone?.trim()),
              then: () =>
                yup
                  .string()
                  .trim()
                  .required(
                    `${t('form_validation_errors.name_required_when_phone_provided')}`,
                  ),
            }),
          phone: phoneSchema,
          type: nameIdNonRequiredSchema.when(['name', 'phone'], {
            is: (name: string | null, phone: string) =>
              Boolean(name) || Boolean(phone?.trim()),
            then: () =>
              nameIdSchemaRequired.required(
                `${t('form_validation_errors.contact_type_required_when_name_or_phone_provided')}`,
              ),
          }),
          isDeleted: yup.boolean().notRequired(),
        },
        [['name', 'phone']],
      ),
    ),
    projectMaterialTotals: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            material: nameIdNonRequiredSchema,
            unitOfMeasure: nameIdNonRequiredSchema.nullable(),
            totalQuantity: yup.string().notRequired(),
            id: yup.string(),
          })
          .notRequired(),
      )
      .notRequired(),
    // The order's own editable material list (additive, layered on top of the
    // scalar primary `material`). isDeleted soft-deletes a persisted row so the
    // deparse can emit { id, _destroy } — react-hook-form's physical remove()
    // alone would not delete a persisted child through nested attributes.
    materialTotals: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            material: nameIdNonRequiredSchema.nullable(),
            unitOfMeasure: nameIdNonRequiredSchema.nullable(),
            totalQuantity: yup.string().notRequired(),
            id: yup.string(),
            isDeleted: yup.boolean().notRequired(),
          })
          .notRequired(),
      )
      .notRequired(),
    allPhaseCodes: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            id: yup.string(),
            name: yup.string(),
            code: yup.string(),
          })
          .notRequired(),
      )
      .notRequired(),
    department: yup
      .object()
      .shape({
        name: yup.string(),
        id: yup.string(),
      })
      .notRequired(),
    name: yup.string().notRequired(),
    poJobNumber: yup.string().notRequired(),
    zone: yup.string().notRequired(),
    externalId: yup.string().notRequired(),
    serviceClass: yup
      .object()
      .shape({
        name: yup.string(),
        id: yup.string(),
      })
      .notRequired(),
    equipmentTypeGrossCapacityUnitOfMeasure: nameIdNonRequiredSchema
      .nullable()
      .when('equipmentTypeGrossCapacity', {
        is: (equipmentTypeGrossCapacity: string | number | null) => {
          return formQuantityFieldRepresentsEmptyQuantity(equipmentTypeGrossCapacity);
        },
        then: () => nameIdNonRequiredSchema.notRequired(),
        otherwise: () => nameIdSchemaRequired.required(),
      }),
    equipmentTypeGrossCapacity: yup.string().notRequired(),
    loadUnitOfMeasure: nameIdSchemaRequired.notRequired(),
    unitsPerHour: yup.lazy((val) => {
      return isNil(val) || String(val).trim() === ''
        ? yup.string().notRequired()
        : yup.number().notRequired();
    }),
    jobUnitOfMeasure: nameIdNonRequiredSchema.nullable().when('jobQuantity', {
      is: (jobQuantity: string | number | null) => {
        return formQuantityFieldRepresentsEmptyQuantity(jobQuantity);
      },
      then: () => nameIdNonRequiredSchema.notRequired(),
      otherwise: () => nameIdSchemaRequired.required(),
    }),
    jobQuantity: yup.number().when('jobUnitOfMeasure', {
      is: (uom: ItemNameAndId | null) =>
        isNil(uom) || uom?.id === OrderUnitOfMeasure.LOAD,
      then: (schema) => {
        return yup.lazy((val) => {
          if (String(val).trim() === '' || isNil(val)) {
            return yup
              .string()
              .notRequired()
              .transform(() => null);
          }
          return schema.min(0).required();
        });
      },
      otherwise: (schema) => {
        return yup.lazy((val) => {
          if (String(val).trim() === '' || isNil(val)) {
            return yup.string().required(
              `${t('form_validation_errors.required', {
                field: t('form_fields.job_quantity'),
              })}`,
            );
          }
          return schema.min(0.01).required();
        });
      },
    }),
    contractNumber: yup.string().notRequired(),
    dispatchNumber: yup.string().notRequired(),
    fileAttachments: fileAttachmentSchema,
    requester: yup.object().shape({ id: yup.string() }).notRequired(),
    estimatedDeliveredQuantity: yup.string().notRequired(),
    cycleTime: emptyStringOrNumberNotRequiredSchema,
    cycleDistance: emptyStringOrNumberNotRequiredSchema,
    pickupOnsite: emptyStringOrNumberNotRequiredSchema,
    dropoffOnsite: emptyStringOrNumberNotRequiredSchema,
    pickupToDropoff: emptyStringOrNumberNotRequiredSchema,
    totalLoads: emptyStringOrNumberNotRequiredSchema,
    recurringLoads: yup.boolean().notRequired(),
    chaseMissingTickets: yup.boolean().notRequired(),
    liveEta: yup.boolean().notRequired(),
    movingSiteSyncEnabled: yup.boolean().notRequired(),
    defaultDigitalTicketView: yup.boolean().notRequired(),
    jobsSplittable: yup.boolean().notRequired(),
    prevailingWage: yup.boolean().notRequired(),
    taxRateSchedule: yup.mixed<TaxRateScheduleFormValue>().nullable().notRequired(),
    taxRate: yup
      .number()
      .transform((_, val) =>
        isNil(val) || String(val).trim() === '' ? undefined : Number(val),
      )
      .min(0)
      .max(100)
      .notRequired()
      .nullable(),
    taxFreight: yup.boolean().notRequired(),
    taxMaterial: yup.boolean().notRequired(),
    taxAddOns: yup.boolean().notRequired(),
    haulerRateType: yup
      .object()
      .shape({ label: yup.string().required(), value: yup.string().required() })
      .notRequired(),
    materialRate: yup.mixed<MaterialRateTypeahead>().notRequired(),
    materialRateUoM: nameIdNonRequiredSchema.nullable().when('materialRateSellPrice', {
      is: (sellPrice: string | number | null | undefined) =>
        !isNil(sellPrice) && String(sellPrice).trim() !== '',
      then: () =>
        nameIdSchemaRequired.required(
          t('form_validation_errors.required', { field: t('form_fields.unit') }),
        ),
      otherwise: () => nameIdNonRequiredSchema.notRequired(),
    }),
    materialRateBuyPrice: yup
      .number()
      .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
      .min(0)
      .typeError('Numbers only.')
      .notRequired(),
    materialRateSellPrice: yup
      .number()
      // Must handle nil explicitly: `String(null)` is `'null'` (non-empty), so
      // a naive `String(val).trim() !== ''` check would cast null/undefined to
      // 0/NaN and leak into sibling `.when()` callbacks (e.g. materialRateUoM).
      .transform((_, val) =>
        isNil(val) || String(val).trim() === '' ? undefined : Number(val),
      )
      .min(0)
      .typeError('Numbers only.')
      .notRequired(),
    vendorPayCommissionPercentage: commissionPercentageSchema,
    driverPayCommissionPercentage: commissionPercentageSchema,
    materialRateItemized: yup.boolean().notRequired(),
    vendorAddOnAssignments: yup.array().notRequired(),
    customerAddOnAssignments: yup.array().notRequired(),
    driverAddOnAssignments: yup.array().notRequired(),
    geofenceDelaySeconds: yup
      .number()
      .transform((_, val) => (String(val).trim() !== '' ? Number(val) : undefined))
      .min(0)
      .typeError('Numbers only.')
      .notRequired(),
    chargeToAccount: yup.string().notRequired(),
    defaultSiteRateScheduleId: yup.string().nullable().notRequired(),
  },
  [['jobUnitOfMeasure', 'jobQuantity']],
);

export const setDefaultOrderValues = (
  defaultOrder: Order,
  copyAndEditValuesOverride?: { requester: UserBasic },
  company?: Nullable<Company>,
  formCompany?: Nullable<Company | CompanyBasic>,
  defaultDate?: string,
  orderFormStartTimeDefaultTo7amFeatureFlagEnabled?: boolean,
  runningDefaultOnSettingEnabled?: boolean,
  tomlinsonOrderModalFeatureFlagEnabled?: boolean,
  chaseMissingTicketsDefaultOnSettingEnabled?: boolean,
  shouldUseKilometers?: boolean,
  orderFormStartTimeDefaultToNullFeatureFlagEnabled?: boolean,
  liveEtaDefault?: boolean,
  recordCompanySettings?: CompanySettings,
) => {
  const pickUpWayPoint = defaultOrder?.pickUpWayPoints?.[0];
  const dropOffWayPoint = defaultOrder?.dropOffWayPoints?.[0];
  const stagingWayPoint = defaultOrder?.stagingWayPoints?.[0];
  let jobStartAt = defaultOrder?.jobStartAt || '';

  let loadAt =
    !defaultOrder && defaultDate
      ? dayjs.tz(defaultDate).set('hours', dayjs().hour()).add(1, 'hours').startOf('hour')
      : defaultOrder?.loadAt || ('' as const);

  if (copyAndEditValuesOverride) {
    loadAt = dayjs.tz().set('hours', dayjs().hour()).add(1, 'hours').startOf('hour');
    jobStartAt = loadAt.clone();
  }

  if (!defaultOrder && orderFormStartTimeDefaultTo7amFeatureFlagEnabled) {
    loadAt = (loadAt || dayjs.tz()).set('hours', 7).startOf('hour');

    if (copyAndEditValuesOverride) {
      jobStartAt = loadAt.clone();
    }
  }

  if (!defaultOrder && orderFormStartTimeDefaultToNullFeatureFlagEnabled) {
    loadAt = '' as const;
    jobStartAt = '';
  }

  return {
    id: defaultOrder?.id || '',
    company: defaultOrder?.company || formCompany || company || null,
    jobStartAt,
    loadAt,
    notes: defaultOrder?.notes || '',
    jobNotes: defaultOrder?.jobNotes || '',
    projectNotes: '',
    orderNotes: defaultOrder?.notes || '',
    internalNotes: defaultOrder?.internalNotes || '',
    jobTime: isNil(defaultOrder?.jobTime) ? null : Number(defaultOrder.jobTime / 60),
    jobQuantity: isNil(defaultOrder?.jobQuantity) ? '' : Number(defaultOrder.jobQuantity),
    jobUnitOfMeasure: defaultOrder?.jobUnitOfMeasure
      ? defaultOrder.jobUnitOfMeasure
      : tomlinsonOrderModalFeatureFlagEnabled || runningDefaultOnSettingEnabled
        ? ItemNameAndId.parse({
            id: OrderUnitOfMeasure.LOAD,
            name: OrderUnitOfMeasure.LOAD,
          })
        : null,
    state: defaultOrder?.state || '',
    truckCount: defaultOrder?.orderId
      ? (defaultOrder?.orderSummary?.jobsCount ?? defaultOrder?.truckCount)
      : defaultOrder?.truckCount || 1,
    truckDelay: defaultOrder?.truckDelay || 0,
    trucksPerDelay: defaultOrder?.trucksPerDelay || 1,
    loadsPerTruck: defaultOrder?.loadsPerTruck ?? '',
    unitOfMeasure: {
      name: defaultOrder?.unitOfMeasure?.name || '',
      id: defaultOrder?.unitOfMeasure?.id || '',
    },
    account: {
      name: defaultOrder?.account?.name || '',
      id: defaultOrder?.account?.id || '',
    },
    orderId: defaultOrder?.orderId || '',
    project: {
      name: defaultOrder?.project?.name || '',
      id: defaultOrder?.project?.id || '',
      externalId: defaultOrder?.project?.externalId || '',
    },
    projectExternalId: defaultOrder?.project?.externalId || null,
    material: defaultOrder?.material || null,
    // The order's saved per-material rows, carried in WITH their ids so an edit
    // upserts them instead of appending a second copy — the deparse keys on
    // `id` to tell a persisted row from a new one, and a row with no id is
    // always an insert. Empty on create, where the project-seeding effect in
    // MaterialsV2 fills the array instead (it skips any order that has an id).
    materialTotals: (defaultOrder?.materialTotals ?? []).map((materialTotal) => ({
      id: materialTotal.id,
      material: materialTotal.material,
      totalQuantity: materialTotal.totalQuantity,
      unitOfMeasure: materialTotal.unitOfMeasure,
    })),
    equipmentType: defaultOrder?.equipmentType || null,
    service: defaultOrder?.service || null,
    quantity: isNil(defaultOrder?.quantity) ? null : Number(defaultOrder.quantity) || '',
    pickUpWayPoint: pickUpWayPoint?.id
      ? {
          id: pickUpWayPoint?.id || '',
          type: pickUpWayPoint?.type || '',
          ordinality: pickUpWayPoint?.ordinality ?? 10,
          contact: {
            name: pickUpWayPoint?.contact?.name || '',
            phone: pickUpWayPoint?.contact?.phone || '',
          },
          site: {
            id: pickUpWayPoint?.site?.id || '',
            name: pickUpWayPoint?.site?.name || '',
            discardedAt: pickUpWayPoint?.site?.discardedAt?.toISOString() ?? null,
          },
        }
      : null,
    dropOffWayPoint: dropOffWayPoint?.id
      ? {
          id: dropOffWayPoint?.id || '',
          type: dropOffWayPoint?.type || '',
          ordinality: dropOffWayPoint?.ordinality ?? 10,
          contact: {
            name: dropOffWayPoint?.contact?.name || '',
            phone: dropOffWayPoint?.contact?.phone || '',
          },
          site: {
            id: dropOffWayPoint?.site?.id || '',
            name: dropOffWayPoint?.site?.name || '',
            discardedAt: pickUpWayPoint?.site?.discardedAt?.toISOString() ?? null,
          },
        }
      : null,
    stagingWayPoint: stagingWayPoint?.id
      ? {
          id: stagingWayPoint?.id || '',
          type: stagingWayPoint?.type || '',
          ordinality: stagingWayPoint?.ordinality ?? 15,
          contact: {
            name: stagingWayPoint?.contact?.name || '',
            phone: stagingWayPoint?.contact?.phone || '',
          },
          site: {
            id: stagingWayPoint?.site?.id || '',
            name: stagingWayPoint?.site?.name || '',
            discardedAt: stagingWayPoint?.site?.discardedAt?.toISOString() ?? null,
          },
        }
      : null,
    allContacts: defaultOrder?.allContacts?.length
      ? defaultOrder.allContacts.map((single) => {
          return {
            id: single.id || undefined,
            name: single.name || '',
            phone: single.phone || '',
            type: single.type || undefined,
            isDeleted: false,
          };
        })
      : [EmptyContactItem],
    phase: {
      id: defaultOrder?.phase?.id || '',
      name: defaultOrder?.phase?.name || '',
      code: defaultOrder?.phase?.code || '',
    },
    phaseCode: defaultOrder?.phase?.code || '',
    phases: defaultOrder?.phases || [],
    department: defaultOrder?.department || {},
    foremen: defaultOrder?.foremen || [],
    collaborators: defaultOrder?.collaborators ?? [],
    salespeople: defaultOrder?.salespeople || [],
    name: defaultOrder?.name || '',
    poJobNumber: defaultOrder?.poJobNumber || '',
    zone: defaultOrder?.zone || '',
    externalId: defaultOrder?.externalId || '',
    serviceClass: defaultOrder?.serviceClass || {},
    equipmentTypeGrossCapacity: defaultOrder?.equipmentTypeGrossCapacity || '',
    equipmentTypeGrossCapacityUnitOfMeasure:
      defaultOrder?.equipmentTypeGrossCapacityUnitOfMeasure || null,
    loadUnitOfMeasure: defaultOrder?.loadUnitOfMeasure || null,
    unitsPerHour: defaultOrder?.unitsPerHour || null,
    contractNumber: defaultOrder?.contractNumber || '',
    dispatchNumber: defaultOrder?.dispatchNumber?.name || '',
    requester: copyAndEditValuesOverride
      ? copyAndEditValuesOverride.requester
      : defaultOrder?.requester || {},
    cycleTime: defaultOrder?.cycleTimeMinutes ?? null,
    cycleDistance: !isNil(defaultOrder?.pickupToDropoffDistanceMeters)
      ? convertMeters(
          defaultOrder.pickupToDropoffDistanceMeters,
          shouldUseKilometers ? UnitOfDistance.KILOMETER : UnitOfDistance.MILE,
        )
      : null,
    pickupOnsite:
      defaultOrder?.pickupOnsiteMinutes ??
      company?.defaultPickupOnsiteMinutes ??
      DEFAULT_CYCLE_ONSITE_DURATION_IN_MINUTES,
    dropoffOnsite:
      defaultOrder?.dropoffOnsiteMinutes ??
      company?.defaultDropoffOnsiteMinutes ??
      DEFAULT_CYCLE_ONSITE_DURATION_IN_MINUTES,
    pickupToDropoff: defaultOrder?.pickupToDropoffTimeMinutes ?? null,
    totalLoads: defaultOrder?.expectedTotalLoads ?? null,
    estimatedDeliveredQuantity: null,
    recurringLoads: isNil(defaultOrder?.recurringLoads)
      ? (runningDefaultOnSettingEnabled ?? false)
      : defaultOrder.recurringLoads,
    chaseMissingTickets: defaultOrder
      ? (defaultOrder.chaseMissingTickets ?? false)
      : (chaseMissingTicketsDefaultOnSettingEnabled ?? false),
    liveEta: defaultOrder ? (defaultOrder.liveEta ?? false) : (liveEtaDefault ?? false),
    movingSiteSyncEnabled: defaultOrder?.movingSiteSyncEnabled ?? true,
    defaultDigitalTicketView: defaultOrder?.defaultDigitalTicketView ?? false,
    jobsSplittable: defaultOrder?.jobsSplittable ?? false,
    prevailingWage: defaultOrder?.prevailingWage ?? false,
    taxRateSchedule: defaultOrder?.taxRateSchedule
      ? toTaxRateScheduleFormValue(defaultOrder.taxRateSchedule)
      : (null as TaxRateScheduleFormValue | null),
    taxRate: defaultOrder?.taxRate ?? null,
    taxFreight: defaultOrder?.taxFreight ?? false,
    taxMaterial: defaultOrder
      ? (defaultOrder.taxMaterial ?? false)
      : Boolean(recordCompanySettings?.isEnabled('require_tax')),
    taxAddOns: defaultOrder?.taxAddOns ?? false,
    haulerRateType: defaultOrder?.haulerRateType
      ? {
          label: getRateTypeLabel(defaultOrder.haulerRateType),
          value: defaultOrder.haulerRateType,
        }
      : null,
    materialRate: defaultOrder?.materialRate ?? null,
    materialRateUoM: defaultOrder?.materialRateUnitOfMeasure
      ? {
          id: defaultOrder.materialRateUnitOfMeasure,
          name: defaultOrder.materialRateUnitOfMeasure,
        }
      : null,
    materialRateBuyPrice: isNil(defaultOrder?.materialRateBuyPrice)
      ? null
      : defaultOrder.materialRateBuyPrice.toFixed(2),
    materialRateSellPrice: isNil(defaultOrder?.materialRateSellPrice)
      ? null
      : defaultOrder.materialRateSellPrice.toFixed(2),
    vendorPayCommissionPercentage: defaultOrder?.vendorPayCommissionPercentage,
    driverPayCommissionPercentage: defaultOrder?.driverPayCommissionPercentage,
    materialRateItemized: defaultOrder
      ? (defaultOrder.materialRateItemized ?? false)
      : Boolean(recordCompanySettings?.isEnabled('itemize_material_rate_default_on')),
    vendorAddOnAssignments: [],
    customerAddOnAssignments: [],
    driverAddOnAssignments: [],
    geofenceDelaySeconds: defaultOrder?.geofenceDelaySeconds ?? null,
    chargeToAccount: defaultOrder?.chargeToAccount ?? null,
    defaultSiteRateScheduleId: defaultOrder?.defaultSiteRateSchedule?.id ?? null,
  };
};
export type NewOrderFormSchemaInterface = yup.InferType<typeof newOrderFormSchema>;

export function makeCustomerSpecificOrderFormSchema(
  tomlinsonOrderFormProjectFirstFeatureFlagEnabled: boolean,
  tomlinsonOrderModalFeatureFlagEnabled: boolean,
  isServiceClassRequired?: boolean,
  requireOrderNameEnabled?: boolean,
  vendorJobSplittingEnabled?: boolean,
  recordCompanySettings?: CompanySettings,
  canManageMaterialRates = true,
) {
  const maxLoadsCount = vendorJobSplittingEnabled
    ? SPLITTABLE_MAX_LOADS_COUNT
    : DEFAULT_MAX_LOADS_COUNT;

  let schema = newOrderFormSchema.clone().shape({
    loadsPerTruck: yup.lazy((val) => {
      return String(val).trim() === ''
        ? yup.string().notRequired()
        : yup
            .number()
            .nullable()
            .min(1, `${t('form_validation_errors.minimum_is_num', { num: 1 })}`)
            .max(
              maxLoadsCount,
              `${t('form_validation_errors.maximum_is_num', { num: maxLoadsCount })}`,
            );
    }),
  });

  if (tomlinsonOrderFormProjectFirstFeatureFlagEnabled) {
    schema = schema.shape({ project: nameIdSchemaRequired.required('') });
  }

  if (isServiceClassRequired) {
    schema = schema.shape({ serviceClass: nameIdSchemaRequired.required() });
  }

  if (recordCompanySettings?.isEnabled('require_department')) {
    schema = schema.shape({ department: makeRequiredDepartmentSchema() });
  }

  if (recordCompanySettings?.isEnabled('require_salesperson')) {
    schema = schema.shape({ salespeople: makeRequiredSalespeopleSchema() });
  }

  if (recordCompanySettings?.isEnabled('require_pickup_site')) {
    schema = schema.shape({ pickUpWayPoint: waypointSchema.required() });
  }

  const isMaterialRateRequiredFor = makeMaterialRateRequirement(
    recordCompanySettings,
    canManageMaterialRates,
  );
  if (isMaterialRateRequiredFor) {
    const materialRateApplies = (
      material: { id?: string | null } | null | undefined,
      department: { name?: string | null } | null | undefined,
    ) => Boolean(material?.id) && isMaterialRateRequiredFor(department);

    schema = schema.shape({
      materialRateUoM: nameIdNonRequiredSchema
        .nullable()
        .when(
          ['materialRateSellPrice', 'material', 'department'],
          ([sellPrice, material, department]) =>
            !isBlankRateValue(sellPrice) || materialRateApplies(material, department)
              ? nameIdSchemaRequired.required(requiredMessage(t('form_fields.unit')))
              : nameIdNonRequiredSchema.notRequired(),
        ),
      materialRateBuyPrice: makeRatePriceSchema().when(
        ['material', 'department'],
        ([material, department], priceSchema) =>
          materialRateApplies(material, department)
            ? priceSchema.required(
                requiredMessage(t('administration.material_rates.buy_price')),
              )
            : priceSchema,
      ),
      materialRateSellPrice: makeRatePriceSchema().when(
        ['material', 'department'],
        ([material, department], priceSchema) =>
          materialRateApplies(material, department)
            ? priceSchema.required(
                requiredMessage(t('administration.material_rates.sell_price')),
              )
            : priceSchema,
      ),
    });
  }

  if (recordCompanySettings?.isEnabled('require_tax')) {
    schema = schema.shape(makeRequiredTaxSchema(t('order.form.tax_rate_schedule')));
  }

  if (requireOrderNameEnabled) {
    schema = schema.shape({
      name: yup
        .string()
        .required(
          t('form_validation_errors.required', { field: t('order.form.order_name') }),
        ),
    });
  }

  if (tomlinsonOrderModalFeatureFlagEnabled) {
    schema = schema
      .shape({
        phaseName: yup.string().min(1).required(),
        phaseCode: yup.string().min(1).required(),
        equipmentType: yup.object().required(''),
        haulerRateType: yup
          .object()
          .shape({ label: yup.string().required(), value: yup.string().required() })
          .required(),
        pickUpWayPoint: waypointSchema.required(),
      })
      .test(
        'at-least-one-contact-type',
        t('form_validation_errors.at_least_one_contact_required'),
        function (values) {
          const hasForemen = Array.isArray(values?.foremen) && values.foremen.length > 0;
          const hasCollaborators =
            Array.isArray(values?.collaborators) && values.collaborators.length > 0;
          const hasContacts =
            Array.isArray(values?.allContacts) &&
            values.allContacts.some(
              (c) => !!c?.name && c.name.trim() !== '' && !c?.isDeleted,
            );

          if (!hasForemen && !hasCollaborators && !hasContacts) {
            return this.createError({
              path: 'foremen',
              message: t('form_validation_errors.at_least_one_contact_required'),
            });
          }

          return true;
        },
      );
  }

  return schema;
}
